import { API } from '../../api';
import { DimaContext } from '../../DimaContext';
import { Divider, PageHeader, Row, Spin, Table, notification } from 'antd';
import { Link, useParams, useRouteMatch } from 'react-router-dom';
import { statusToColorMap } from '../../consts';
import React, { useContext, useEffect, useState } from 'react';

const ScheduleDetail = () => {
  const context = useContext(DimaContext);
  const { schedule_id: scheduleId } = useParams();
  const [schedule, setSchedule] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const { url } = useRouteMatch();

  const fetchScheduleById = async () => {
    setLoading(true);

    try {
      const { data } = await API.get(`/dis/schedules/${scheduleId}?clientId=${context.client.id}`);
      setSchedule(data);
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to get schedule',
        duration: 3,
        placement: 'topLeft',
      });
    }

    setLoading(false);
  };

  const fetchJobsByScheduleId = async () => {
    setLoading(true);

    try {
      const { data } = await API.get(
        `/dis/jobs?clientId=${context.client.id}&scheduleId=${scheduleId}`,
      );
      setJobs(data.results);
    } catch (err) {
      notification.error({
        message: 'Error',
        description: `Failed to get jobs for schedule ${scheduleId}`,
        duration: 3,
        placement: 'topLeft',
      });
    }

    setLoading(false);
  };

  const generateColumns = () => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: jobId => {
          const urlBase = url.slice(0, url.indexOf('/schedule'));
          const linkUrl = `${urlBase}/job/${jobId}`;
          return (
            <Link to={linkUrl} data-cy={`job-link-${jobId}`}>
              {jobId}
            </Link>
          );
        },
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: (a, b) => {
          if (a.created_at === null || b.created_at === null) return 1;
          return new Date(a.created_at) - new Date(b.created_at);
        },
        render: createdAt => {
          return <span>{new Date(createdAt).toUTCString()}</span>;
        },
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        sorter: (a, b) => {
          if (a.updated_at === null || b.updated_at === null) return 1;
          return new Date(a.updated_at) - new Date(b.updated_at);
        },
        defaultSortOrder: 'descend',
        render: updatedAt => {
          return <span>{new Date(updatedAt).toUTCString()}</span>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: (a, b) => {
          if (a.status === null || b.status === null) return 1;
          return a.status.localeCompare(b.status);
        },
        render: status => {
          return (
            <span
              style={{
                border: '1px solid',
                borderColor: statusToColorMap[status].border,
                padding: '5px 10px',
                backgroundColor: statusToColorMap[status].background,
                color: statusToColorMap[status].color,
              }}
            >
              {' '}
              {status}{' '}
            </span>
          );
        },
      },
    ];
  };

  const jobTypeLink = jobType => {
    const urlBase = url.slice(0, Math.max(0, url.indexOf('/schedule')));
    const linkUrl = `${urlBase}/jobType/${jobType.name}`;
    return (
      <p>
        <span style={{ fontWeight: 'bold' }}>Job Type: </span>
        <Link to={linkUrl} data-cy={`jobType-link-${jobType.name}`}>
          {jobType.name}
        </Link>
      </p>
    );
  };

  useEffect(() => {
    fetchScheduleById();
    fetchJobsByScheduleId();
  }, [scheduleId]);

  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Schedule Detail"
      />
      <Spin spinning={loading}>
        <Row>
          <p>
            <span style={{ fontWeight: 'bold' }}>Name: </span>
            {schedule?.name}
          </p>
        </Row>
        {schedule?.jobType && <div>{jobTypeLink(schedule?.jobType)}</div>}
      </Spin>
      <Divider>Triggered Jobs</Divider>
      <Table
        dataSource={[...jobs]}
        columns={generateColumns()}
        pagination={{ pageSize: 100 }}
        loading={loading}
        showExpandColumn={false}
        rowClassName={row => (row.active === false ? 'table-row-dark ' : 'table-row-light')}
      />
    </>
  );
};

export default ScheduleDetail;
